import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Table,
  Typography,
  message,
  Spin
} from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setBreadcrumbItems } from "../../store/actions";
import { debounce } from "lodash";

const { Text } = Typography;
const { Option } = Select;

const getQuarterMonths = (quarter) => {
  const map = {
    1: ["Apr", "May", "Jun"],
    2: ["Jul", "Aug", "Sep"],
    3: ["Oct", "Nov", "Dec"],
    4: ["Jan", "Feb", "Mar"],
  };
  return map[quarter] || [];
};

const generateQuarterOptions = () => {
  const year = new Date().getFullYear();
  return [1, 2, 3, 4].map((q) => ({
    value: `${q}-${year}`,
    label: `Q${q} ${year} (${getQuarterMonths(q).join(", ")})`,
  }));
};


const quarterOptions = generateQuarterOptions();

const getCurrentQuarterValue = () => {
  const month = new Date().getMonth() + 1;
  let q = 4;
  if (month >= 4 && month <= 6) q = 1;
  else if (month >= 7 && month <= 9) q = 2;
  else if (month >= 10 && month <= 12) q = 3;
  return `${q}-${new Date().getFullYear()}`;
};

const AddRegion = ({ setBreadcrumbItems }) => {
  const navigate = useNavigate();
  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;

  const breadcrumbItems = [
    { title: "Region", link: "#" },
    { title: "Region Listing", link: "#" },
    { title: "Add Region", link: "#" }
  ];

  useEffect(() => {
    setBreadcrumbItems("Region", breadcrumbItems);
  }, [setBreadcrumbItems, breadcrumbItems]);

  const [regionName, setRegionName] = useState("");
  const [regionCode, setRegionCode] = useState("");
  const [regionBudget, setRegionBudget] = useState(0);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [addressId, setAddressId] = useState("");
  const [so, setSo] = useState(0);
  const [outletCoverage, setOutletCoverage] = useState(0);
  const [ubo, setUbo] = useState(0);
  const [quarter, setQuarter] = useState(getCurrentQuarterValue());
  const [products, setProducts] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [addresses, setAddresses] = useState([]);

  // New State/City Logic
  const [stateList, setStateList] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [fetchingCity, setFetchingCity] = useState(false);

  useEffect(() => {
    // Hardcoded list of Indian States
    const states = [
      "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
      "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli", "Daman and Diu", "Delhi", "Goa",
      "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka",
      "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
      "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
      "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
    ];
    setStateList(states);

    fetchProducts();
    fetchAddresses();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASEURL}/product`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data || []);
    } catch (err) {
      message.error("Failed to fetch products");
    }
  };

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASEURL}/address`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(res.data || []);
    } catch (err) {
      message.error("Failed to fetch addresses");
    }
  };

  // Photon Autocomplete Logic
  const fetchCityList = async (value) => {
    if (!value) return;
    setFetchingCity(true);
    setCityOptions([]);
    try {
      const query = `${value}, ${state}`;
      const res = await axios.get(`https://photon.komoot.io/api/`, {
        params: { q: query, limit: 10, lang: 'en' }
      });

      const features = res.data.features || [];
      const options = features.map(item => {
        const props = item.properties;
        const name = props.name;
        const placeState = props.state || '';
        const district = props.district || props.city || '';
        const label = `${name}${district ? `, ${district}` : ''}${placeState ? `, ${placeState}` : ''}`;
        return { label, value: name };
      });

      const uniqueOptions = [...new Map(options.map(item => [item.label, item])).values()];
      setCityOptions(uniqueOptions);

    } catch (e) {
      console.error(e);
    } finally {
      setFetchingCity(false);
    }
  };

  const debouncedFetchCity = useMemo(() => debounce(fetchCityList, 400), [state]);

  const handleStateChange = (selectedState) => {
    setState(selectedState);
    setCity("");
    setCityOptions([]);
  };

  const updateQuantity = (productId, value) => {
    setProductQuantities((prev) => ({
      ...prev,
      [productId]: Math.max(0, value || 0),
    }));
  };

  const calculateUsedBudget = () => {
    return products.reduce((total, p) => {
      const qty = productQuantities[p._id] || 0;
      return total + qty * p.price;
    }, 0);
  };

  const availableBudget = regionBudget - calculateUsedBudget();

  const handleSubmit = async () => {
    if (!regionName || !regionCode || !state || !city || !addressId) {
      return message.warning("Please fill all required fields");
    }

    const currentQuarter = getCurrentQuarterValue();
    if (quarter !== currentQuarter) {
      return message.error("You have not selected the current quarter");
    }

    const formattedQuarter = quarterOptions.find(q => q.value === quarter)?.label || "";

    const cleanPayload = {
      regionCode,
      regionName,
      regionBudget,
      utilizedBudget: calculateUsedBudget(),
      city,
      state,
      so,
      outletCoverage,
      ubo,
      address: [addressId],
      quarter: formattedQuarter,
      product: products.map((p) => ({
        _id: p._id,
        productName: p.productName,
        quantity: productQuantities[p._id] || 0,
        price: p.price,
      })),
      productHistory: [
        {
          product: products.map((p) => ({
            _id: p._id,
            productName: p.productName,
            quantity: productQuantities[p._id] || 0,
            price: p.price,
          })),
          quarter: formattedQuarter,
          version: 1,
        },
      ],
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_BASEURL}/region`, cleanPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      message.success("Region added successfully");
      navigate("/region-listing");
    } catch (err) {
      message.error("Failed to add region");
    }
  };

  const columns = [
    {
      title: "Product Name",
      dataIndex: "productName",
    },
    {
      title: "Price",
      dataIndex: "price",
      render: (price) => `₹${price}`,
    },
    {
      title: "Quantity",
      render: (_, record) => (
        <InputNumber
          min={0}
          value={productQuantities[record._id] || 0}
          onChange={(val) => updateQuantity(record._id, val)}
          controls
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  return (
    <div className="p-4">
      <Card title="Add Region">
        <Row gutter={16}>
          <Col span={8}>
            <Text strong>Region Name</Text>
            <Input value={regionName} onChange={(e) => setRegionName(e.target.value)} />
          </Col>
          <Col span={8}>
            <Text strong>Region Code</Text>
            <Input value={regionCode} onChange={(e) => setRegionCode(e.target.value)} />
          </Col>
          <Col span={8}>
            <Text strong>Total Budget</Text>
            <InputNumber min={0} style={{ width: "100%" }} value={regionBudget} onChange={setRegionBudget} controls />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={8}>
            <Text strong>State</Text>
            <Select showSearch value={state} onChange={handleStateChange} style={{ width: "100%" }} placeholder="Select State" filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
              {stateList.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>City (Type to search)</Text>
            <Select
              showSearch
              value={city}
              onChange={setCity}
              disabled={!state}
              style={{ width: "100%" }}
              onSearch={debouncedFetchCity}
              placeholder={state ? "Start typing to find City..." : "Select State first"}
              filterOption={false} // Server side search
              notFoundContent={fetchingCity ? <Spin size="small" /> : "Type to search..."}
            >
              {cityOptions.map((c) => (
                <Option key={c.value} value={c.value}>{c.label}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>Address</Text>
            <Select showSearch value={addressId} onChange={setAddressId} style={{ width: "100%" }} placeholder="Select Address" filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
              {addresses.map((a) => (
                <Option key={a._id} value={a._id}>{a.addressDetails}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={8}>
            <Text strong>SO</Text>
            <InputNumber min={0} value={so} onChange={setSo} style={{ width: "100%" }} controls />
          </Col>
          <Col span={8}>
            <Text strong>Outlet Coverage</Text>
            <InputNumber min={0} value={outletCoverage} onChange={setOutletCoverage} style={{ width: "100%" }} controls />
          </Col>
          <Col span={8}>
            <Text strong>UBO</Text>
            <InputNumber min={0} value={ubo} onChange={setUbo} style={{ width: "100%" }} controls />
          </Col>
        </Row>

        <Row style={{ marginTop: 20 }}>
          <Col span={24}>
            <Row><Text strong>Quarter</Text>
            </Row>

            <Row style={{ marginTop: 10 }}>
              <Select
                showSearch
                value={quarter}
                onChange={setQuarter}
                style={{ width: 200, marginBottom: 8 }}
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {quarterOptions.map((q) => (
                  <Option key={q.value} value={q.value}>
                    {q.label}
                  </Option>
                ))}
              </Select>
            </Row>

          </Col>
        </Row>

        <Row style={{ marginTop: 10 }}>
          <Text strong>
            Available Budget : ₹{availableBudget.toLocaleString()}
          </Text>
        </Row>

        <Row style={{ marginTop: 20 }}>
          <Col span={24}>
            <Table columns={columns} dataSource={products} rowKey="_id" pagination={false} />
          </Col>
        </Row>

        <Row justify="start" style={{ marginTop: 20 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleSubmit} style={{ backgroundColor: PRIMARY_RED, borderColor: PRIMARY_RED }}>
            Add Region
          </Button>
          <Button type="link" style={{ color: PRIMARY_RED, marginLeft: 10 }} icon={<ArrowLeftOutlined />} onClick={() => navigate("/region-listing")}>
            Back
          </Button>
        </Row>
      </Card>
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(AddRegion);
